/**
 * Scale Degree Drill — free-practice mode.
 *
 * Cadence establishes the key, one note plays, user identifies the solfège
 * syllable. Self-directed: no session machine, no FSRS by default.
 */

import {
  useReducer,
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CoachSessionDoneCard } from "../components/CoachSessionDone.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { ModeSwitcherBar, MODE_COLOR as PICKER_MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";
import { ChallengeTimer } from "../components/ChallengeTimer.js";
import { ChallengeScoreCard } from "../components/ChallengeScoreCard.js";
import { useListenSession } from "../lib/use-listen-session.js";
import { useExerciseSession } from "../lib/use-exercise-session.js";
import { ListenAutoAdvanceControl } from "../components/ListenAutoAdvanceControl.js";
import { useChallengeTimer } from "../lib/use-challenge-timer.js";
import { nextRoundSeed } from "../lib/round-seed.js";
import {
  buildDrillRound,
  CADENCE_TEMPO_OPTS,
  DEFAULT_DRILL_CONFIG,
  drillItemId,
  drillStatsKey,
  SCALE_SPECS,
  type DrillConfig,
  type DrillRound,
  type ExerciseScaleType,
  type Exercise,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import { SolfegeButtons, StagePips } from "@music-app/ui-components";
import { useCardsStore, usePrefsStore, getDrillStats, upsertDrillStats, getChallengeData, recordChallengeAttempt, recordReview as dbRecordReview, type DrillStatsRecord } from "@music-app/persistence";
import { exerciseReviewSource, type Rating } from "@music-app/learning-system";
import { Score } from "@music-app/notation";
import { createUISoundPlayer, type UISoundPlayer } from "../lib/ui-sounds.js";
import { VocalSinger } from "./VocalSinger.js";
import {
  createMidiInput,
  type MidiInput,
  type MidiNoteOn,
} from "@music-app/input-midi";
import { gradeMidiAnswer } from "./midi-grading.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const CADENCE_PIP_COUNT = 4;

// ─── Scale type UI metadata ───────────────────────────────────────────────────

interface ScaleTypeInfo {
  /** Default fixed-tonic MIDI. */
  midi: number;
  /** Button label for the fixed-tonic toggle, e.g. "C (Do)" or "A (La)". */
  label: string;
  /** True for 5-note scales: hides range / octave settings. */
  isPentatonic: boolean;
  /** Closest tonality for VocalSinger / MIDI grading helpers. */
  tonalityMode: "major" | "minor";
}

const SCALE_TYPE_INFO: Record<ExerciseScaleType, ScaleTypeInfo> = {
  "major":               { midi: 60, label: "C (Do)", isPentatonic: false, tonalityMode: "major" },
  "natural-minor":       { midi: 69, label: "A (Do)", isPentatonic: false, tonalityMode: "minor" },
  "harmonic-minor":      { midi: 69, label: "A (Do)", isPentatonic: false, tonalityMode: "minor" },
  "melodic-minor":       { midi: 69, label: "A (Do)", isPentatonic: false, tonalityMode: "minor" },
  "dorian":              { midi: 62, label: "D (Do)", isPentatonic: false, tonalityMode: "major" },
  "phrygian":            { midi: 64, label: "E (Do)", isPentatonic: false, tonalityMode: "major" },
  "lydian":              { midi: 65, label: "F (Do)", isPentatonic: false, tonalityMode: "major" },
  "mixolydian":          { midi: 67, label: "G (Do)", isPentatonic: false, tonalityMode: "major" },
  "locrian":             { midi: 71, label: "B (Do)", isPentatonic: false, tonalityMode: "major" },
  "phrygian-dominant":   { midi: 64, label: "E (Do)", isPentatonic: false, tonalityMode: "major" },
  "lydian-dominant":     { midi: 67, label: "G (Do)", isPentatonic: false, tonalityMode: "major" },
  "major-pentatonic":    { midi: 60, label: "C (Do)", isPentatonic: true,  tonalityMode: "major" },
  "minor-pentatonic":    { midi: 69, label: "A (Do)", isPentatonic: true,  tonalityMode: "minor" },
  "blues":               { midi: 60, label: "C (Do)", isPentatonic: true,  tonalityMode: "minor" },
  "whole-tone":          { midi: 60, label: "C (Do)", isPentatonic: true,  tonalityMode: "major" },
};

/**
 * Scale-degree lesson for a given scale type. Major-family scales point at the
 * major lesson, the three minor scales at the minor lesson; modes, pentatonics,
 * and symmetric scales have no scale-degree lesson, so the link hides rather
 * than mislinking to the major one. `ExerciseLessonLink` drops nodes with no
 * authored lesson, so this is a preference, not a guarantee.
 */
function lessonNodesForScaleType(scaleType: ExerciseScaleType): string[] {
  switch (scaleType) {
    case "major":
      return ["diatonic-degrees-major"];
    case "natural-minor":
    case "harmonic-minor":
    case "melodic-minor":
      return ["diatonic-degrees-minor"];
    default:
      return [];
  }
}

/** Grouped display for the config screen. */
const SCALE_TYPE_GROUPS: Array<{ label: string; types: ExerciseScaleType[] }> = [
  { label: "Natural",          types: ["major", "natural-minor"] },
  { label: "Minor variants",   types: ["harmonic-minor", "melodic-minor"] },
  { label: "Modes",            types: ["dorian", "phrygian", "lydian", "mixolydian", "locrian"] },
  { label: "Chromatic modes",  types: ["phrygian-dominant", "lydian-dominant"] },
  { label: "Pentatonic",       types: ["major-pentatonic", "minor-pentatonic"] },
  { label: "Blues & Symmetric", types: ["blues", "whole-tone"] },
];

// ─── State machine ────────────────────────────────────────────────────────────

type DrillPhase =
  | "config"    // configuration screen
  | "playing"   // cadence + note is playing
  | "awaiting"  // waiting for user answer (graded mode)
  | "reveal"    // correct answer shown immediately (listening mode)
  | "graded"    // answer submitted; correct/wrong feedback shown
  | "done";     // all rounds completed; session summary

interface DegreeStat {
  correct: number;
  played: number;
}

interface DrillState {
  phase: DrillPhase;
  config: DrillConfig;
  sessionLength: number;
  round: DrillRound | null;
  roundNumber: number;    // 1-indexed; current round in progress
  correctCount: number;   // graded mode only
  selectedIndex: number | null;
  wasCorrect: boolean | null;
  wasTimeout: boolean;
  sessionStats: Partial<Record<number, DegreeStat>>;
}

type DrillAction =
  | { type: "START"; config: DrillConfig; sessionLength: number; round: DrillRound }
  | { type: "PLAYBACK_DONE" }
  | { type: "SELECT"; index: number }
  | { type: "TIMEOUT" }
  | { type: "NEXT_ROUND"; round: DrillRound }
  | { type: "SESSION_DONE" }
  | { type: "STOP" };

const INITIAL_STATE: DrillState = {
  phase: "config",
  config: DEFAULT_DRILL_CONFIG,
  sessionLength: 10,
  round: null,
  roundNumber: 0,
  correctCount: 0,
  selectedIndex: null,
  wasCorrect: null,
  wasTimeout: false,
  sessionStats: {},
};

function addDegreeStat(
  stats: Partial<Record<number, DegreeStat>>,
  degree: number,
  correct: boolean | null,
): Partial<Record<number, DegreeStat>> {
  const prev = stats[degree] ?? { correct: 0, played: 0 };
  return {
    ...stats,
    [degree]: {
      played: prev.played + 1,
      correct: prev.correct + (correct === true ? 1 : 0),
    },
  };
}

function drillReducer(state: DrillState, action: DrillAction): DrillState {
  switch (action.type) {
    case "START":
      return {
        ...INITIAL_STATE,
        phase: "playing",
        config: action.config,
        sessionLength: action.sessionLength,
        round: action.round,
        roundNumber: 1,
      };
    case "PLAYBACK_DONE":
      if (state.phase !== "playing") return state;
      if (state.config.drillMode === "listening" && state.round) {
        return {
          ...state,
          phase: "reveal",
          sessionStats: addDegreeStat(state.sessionStats, state.round.correctDegree, null),
        };
      }
      return { ...state, phase: "awaiting" };
    case "SELECT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      const correct = state.round.syllables[action.index] === state.round.correctSyllable;
      return {
        ...state,
        phase: "graded",
        selectedIndex: action.index,
        wasCorrect: correct,
        correctCount: state.correctCount + (correct ? 1 : 0),
        sessionStats: addDegreeStat(state.sessionStats, state.round.correctDegree, correct),
      };
    }
    case "TIMEOUT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      return {
        ...state,
        phase: "graded",
        wasCorrect: false,
        wasTimeout: true,
        sessionStats: addDegreeStat(state.sessionStats, state.round.correctDegree, false),
      };
    }
    case "NEXT_ROUND":
      return {
        ...state,
        phase: "playing",
        round: action.round,
        roundNumber: state.roundNumber + 1,
        selectedIndex: null,
        wasCorrect: null,
        wasTimeout: false,
      };
    case "SESSION_DONE":
      return { ...state, phase: "done" };
    case "STOP":
      return { ...INITIAL_STATE };
  }
}

const SESSION_LENGTH_PRESETS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

// ─── Config persistence ───────────────────────────────────────────────────────

const SCALE_DRILL_LS_KEY = "scale-drill-config-v2";

interface ScaleExerciseSavedConfig {
  sessionLength: number;
  mode: ExerciseMode;
  scaleType: ExerciseScaleType;
  selectedKey: string | null;
}

const SCALE_DRILL_DEFAULTS: ScaleExerciseSavedConfig = {
  sessionLength: DEFAULT_SESSION_LENGTH,
  mode: "practice",
  scaleType: "major",
  selectedKey: null,
};

function loadScaleExerciseConfig(): ScaleExerciseSavedConfig {
  try {
    const raw = localStorage.getItem(SCALE_DRILL_LS_KEY);
    if (raw) return { ...SCALE_DRILL_DEFAULTS, ...(JSON.parse(raw) as Partial<ScaleExerciseSavedConfig>) };
  } catch { /* ignore */ }
  return SCALE_DRILL_DEFAULTS;
}

function saveScaleExerciseConfig(cfg: ScaleExerciseSavedConfig): void {
  try { localStorage.setItem(SCALE_DRILL_LS_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

// ─── Helper: synthesize Exercise from DrillRound ─────────────────────────────

/**
 * Build a minimal Exercise from a DrillRound + DrillConfig so VocalSinger
 * and MIDI grading helpers — which expect an Exercise — can be reused
 * without changes.
 */
function drillRoundToExercise(round: DrillRound, config: DrillConfig): Exercise {
  const { tonalityMode } = SCALE_TYPE_INFO[config.scaleType];
  return {
    itemId: drillItemId(config.scaleType, round.correctDegree),
    exerciseType: "scale-degree-recognition",
    audio: round.audioPlan,
    prompt: "What scale degree did you hear?",
    options: (round.syllables as string[]).map((syl, i) => ({
      id: `degree-${i + 1}`,
      label: syl,
    })),
    correctAnswerId: `degree-${round.correctDegree}`,
    meta: {
      seed: 0,
      explanation: `Scale degree ${round.correctDegree} (${round.correctSyllable}) in ${round.keyName}.`,
      distractorCount: round.syllables.length - 1,
      tonicMidi: round.tonicMidi,
      tonalityMode,
      teachAudio: round.teachAudio,
      keyName: round.keyName,
    },
  };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ScaleExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const VALID_MODES: ExerciseMode[] = ["listen", "practice", "review", "challenge"];
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: ExerciseMode = rawMode && VALID_MODES.includes(rawMode) ? rawMode : "practice";
  const listenMode = mode === "listen";
  const challengeMode = mode === "challenge";
  const practiceMode = mode === "practice";
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const coached = searchParams.get("coached") === "true";
  const coachItemCount = Math.max(1, parseInt(searchParams.get("itemCount") ?? "10", 10));
  const from = searchParams.get("from") ?? undefined;

  const [state, dispatch] = useReducer(drillReducer, INITIAL_STATE);
  const playerRef = useRef<AudioPlayer | null>(null);
  const [audioPlayer, setAudioPlayer] = useState<AudioPlayer | null>(null);
  const uiSoundRef = useRef<UISoundPlayer | null>(null);
  const midiRef = useRef<MidiInput | null>(null);
  const midiNotesRef = useRef<MidiNoteOn[]>([]);
  const midiSubmittedRef = useRef(false);
  const bassIndexRef = useRef<number>(0);

  const [activePip, setActivePip] = useState<number | null>(null);
  const [donePips, setDonePips] = useState<number[]>([]);

  const { recordPlay } = useListenSession("scales", listenMode, nodeId);
  const { recordItem } = useExerciseSession("scales", mode === "review" ? "practice" : "drill", !listenMode && !challengeMode);

  const challengeStartedAtRef = useRef(new Date().toISOString());
  const challengeCorrectRef = useRef(0);
  const challengeResponseTimesRef = useRef<number[]>([]);
  const responseStartRef = useRef(0);
  const previousBestRef = useRef<number | null | undefined>(undefined);

  const applyReview = useCardsStore((s) => s.applyReview);
  const instrument = usePrefsStore((s) => s.instrument);
  const uiSoundsEnabled = usePrefsStore((s) => s.uiSoundsEnabled);
  const cadenceTempo = usePrefsStore((s) => s.cadenceTempo);
  const vocalModeEnabled = usePrefsStore((s) => s.vocalModeEnabled);
  const midiInputEnabled = usePrefsStore((s) => s.midiInputEnabled);
  const midiSelectedDeviceId = usePrefsStore((s) => s.midiSelectedDeviceId);

  // Create player on mount; tear down on unmount.
  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;
    setAudioPlayer(player);
    return () => {
      player.stop();
      player.destroy();
      uiSoundRef.current?.destroy();
      midiRef.current?.destroy();
    };
  }, []);

  // Load preferred instrument whenever it changes.
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !instrument) return;
    void (async () => {
      await player.init();
      if (!player.isInstrumentLoaded(instrument)) {
        await player.loadInstrument(instrument);
      }
      player.setInstrument(instrument);
    })();
  }, [instrument]);

  // Tear down MIDI when the user disables MIDI mode.
  useEffect(() => {
    if (midiInputEnabled) return;
    midiRef.current?.destroy();
    midiRef.current = null;
    midiNotesRef.current = [];
  }, [midiInputEnabled]);

  // Reset MIDI buffer at every round boundary.
  useEffect(() => {
    midiNotesRef.current = [];
    midiSubmittedRef.current = false;
  }, [state.round]);

  // Load previous challenge best once on mount in challenge mode.
  useEffect(() => {
    if (!challengeMode) return;
    challengeStartedAtRef.current = new Date().toISOString();
    challengeCorrectRef.current = 0;
    challengeResponseTimesRef.current = [];
    void getChallengeData("scales").then((d) => { previousBestRef.current = d.best; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Record response start time when awaiting phase begins (for all graded modes).
  useEffect(() => {
    if (state.phase !== "awaiting") return;
    responseStartRef.current = Date.now();
  }, [state.phase]);

  // FSRS write + review record after grading (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    if (state.config.drillMode === "listening") return;
    const itemId = drillItemId(state.config.scaleType, state.round.correctDegree);
    const correct = state.wasCorrect === true;
    const responseMs = Date.now() - responseStartRef.current;
    const chosenAnswerId =
      !correct && state.selectedIndex !== null ? `degree-${state.selectedIndex + 1}` : null;
    // fsrsEnabled encodes practice/challenge at start time; challengeMode is from URL.
    const source = exerciseReviewSource({ challengeMode, fsrsEnabled: state.config.fsrsEnabled });
    void dbRecordReview({
      itemId,
      sessionId: "",
      rating: correct ? "good" : "again",
      correct,
      responseMs,
      chosenAnswerId,
      source,
    });
    if (challengeMode) {
      if (correct) challengeCorrectRef.current++;
      challengeResponseTimesRef.current.push(responseMs);
      const rating: Rating = correct ? (responseMs < 5000 ? "good" : "hard") : "again";
      void applyReview(itemId, rating);
      return;
    }
    if (!state.config.fsrsEnabled) return;
    void applyReview(itemId, correct ? "good" : "again");
  }, [state.phase, state.round, state.wasCorrect, state.selectedIndex, state.config.fsrsEnabled, state.config.scaleType, state.config.drillMode, challengeMode, applyReview]);

  // Record challenge attempt on session end.
  useEffect(() => {
    if (!challengeMode || state.phase !== "done") return;
    const correct = challengeCorrectRef.current;
    const times = challengeResponseTimesRef.current;
    const avg = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    void recordChallengeAttempt({
      challengeKey: "scales",
      startedAt: challengeStartedAtRef.current,
      items: state.sessionLength,
      correct,
      accuracy: state.sessionLength > 0 ? correct / state.sessionLength : 0,
      averageResponseMs: avg,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeMode, state.phase]);

  // Auto-advance 600ms after timeout.
  useEffect(() => {
    if (!state.wasTimeout) return;
    const t = window.setTimeout(() => nextRound(), 600);
    return () => window.clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.wasTimeout]);

  // Drill stats write after each graded answer (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    void upsertDrillStats(
      drillStatsKey(state.config),
      "graded",
      state.wasCorrect ?? false,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // Drill stats write after each listening-mode reveal (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "reveal" || !state.round) return;
    void upsertDrillStats(drillStatsKey(state.config), "listening");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // Record item play on each listen reveal.
  useEffect(() => {
    if (state.phase !== "reveal" || !listenMode || !state.round) return;
    recordPlay(drillItemId(state.config.scaleType, state.round.correctDegree));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // ── Note-start / note-end callbacks for StagePips ──

  const handleNoteStart = useCallback((event: { midi: number; voice?: string }) => {
    if (event.voice === "bass") {
      const idx = bassIndexRef.current;
      bassIndexRef.current = idx + 1;
      setActivePip(idx);
    }
  }, []);

  const handleNoteEnd = useCallback((event: { midi: number; voice?: string }) => {
    if (event.voice === "bass") {
      setActivePip(null);
      setDonePips((d) => [...d, d.length]);
    }
  }, []);

  // ── Imperative playback helpers ──

  const playRound = useCallback(async (round: DrillRound, drillMode: DrillConfig["drillMode"]) => {
    const player = playerRef.current;
    if (!player) return;
    player.stop();
    setActivePip(null);
    setDonePips([]);
    bassIndexRef.current = 0;
    await player.init();
    const pref = usePrefsStore.getState().instrument;
    if (!player.isInstrumentLoaded(pref)) await player.loadInstrument(pref);
    player.setInstrument(pref);
    await player.play(round.audioPlan, {
      onNoteStart: handleNoteStart,
      onNoteEnd: handleNoteEnd,
    });
    setActivePip(null);

    // In listening mode: wait 2 seconds, then reveal the answer and start the
    // resolution walk simultaneously so the user hears how the degree resolves
    // while reading the answer.
    if (drillMode === "listening") {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 2000));
      if (round.teachAudio) {
        await player.play(round.teachAudio);
      }
    }

    recordItem(drillItemId(state.config.scaleType, round.correctDegree));
    dispatch({ type: "PLAYBACK_DONE" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleNoteStart, handleNoteEnd, recordItem, state.config.scaleType]);

  const replay = useCallback(() => {
    const player = playerRef.current;
    if (!player || !state.round) return;
    void player.play(state.round.audioPlan);
  }, [state.round]);

  const startDrill = useCallback(
    (config: DrillConfig, sessionLength: number) => {
      const round = buildDrillRound(config, nextRoundSeed(), CADENCE_TEMPO_OPTS[cadenceTempo]);
      dispatch({ type: "START", config, sessionLength, round });
      void playRound(round, config.drillMode);
    },
    [playRound, cadenceTempo],
  );

  const nextRound = useCallback(() => {
    if (!state.round) return;
    if (state.roundNumber >= state.sessionLength) {
      dispatch({ type: "SESSION_DONE" });
      return;
    }
    const round = buildDrillRound(state.config, nextRoundSeed(), CADENCE_TEMPO_OPTS[cadenceTempo]);
    dispatch({ type: "NEXT_ROUND", round });
    void playRound(round, state.config.drillMode);
  }, [state.config, state.round, state.roundNumber, state.sessionLength, playRound, cadenceTempo]);

  const handleSelect = useCallback((index: number) => {
    if (!state.round || state.phase !== "awaiting") return;
    const correct = (state.round.syllables[index] as string) === state.round.correctSyllable;

    dispatch({ type: "SELECT", index });

    if (uiSoundsEnabled) {
      if (!uiSoundRef.current) uiSoundRef.current = createUISoundPlayer();
      uiSoundRef.current.play(correct ? "correct" : "wrong");
    }

    if (!correct && state.round.teachAudio) {
      const teachPlan = state.round.teachAudio;
      const player = playerRef.current;
      if (player) {
        window.setTimeout(() => {
          void player.play(teachPlan);
        }, 600);
      }
    }
  }, [state.round, state.phase, uiSoundsEnabled]);

  const handleVocalAnswer = useCallback((optionId: string) => {
    const degree = parseInt(optionId.slice("degree-".length), 10);
    handleSelect(degree - 1);
  }, [handleSelect]);

  const handleTimeout = useCallback(() => {
    dispatch({ type: "TIMEOUT" });
  }, []);

  const { remainingMs: challengeRemainingMs } = useChallengeTimer({
    active: challengeMode && state.phase === "awaiting",
    timeMs: 8000,
    onTimeout: handleTimeout,
  });

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
    dispatch({ type: "STOP" });
  }, []);

  // ── MIDI subscription: active only while in "awaiting" phase ──

  useEffect(() => {
    if (!midiInputEnabled) return;
    if (state.phase !== "awaiting" || !state.round) return;

    const syntheticExercise = drillRoundToExercise(state.round, state.config);
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    void (async () => {
      let m = midiRef.current;
      if (!m) {
        m = createMidiInput();
        try {
          await m.init();
        } catch {
          m.destroy();
          return;
        }
        if (cancelled) { m.destroy(); return; }
        midiRef.current = m;
        m.selectDevice(midiSelectedDeviceId);
      } else {
        m.selectDevice(midiSelectedDeviceId);
      }

      unsubscribe = m.onNoteOn((event) => {
        if (cancelled || midiSubmittedRef.current) return;
        midiNotesRef.current.push(event);
        const result = gradeMidiAnswer(midiNotesRef.current, syntheticExercise);
        if (!result) return;
        midiSubmittedRef.current = true;
        const degree = parseInt(result.optionId.slice("degree-".length), 10);
        handleSelect(degree - 1);
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [midiInputEnabled, state.phase, state.round, state.config, midiSelectedDeviceId]);

  // ── Keyboard shortcuts ──

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key.toLowerCase() === "r" && state.phase !== "config" && state.phase !== "done") {
        e.preventDefault();
        replay();
        return;
      }
      if (state.phase === "awaiting") {
        const n = parseInt(e.key, 10);
        if (n >= 1 && n <= 7) {
          e.preventDefault();
          handleSelect(n - 1);
        }
      }
      if (state.phase === "graded" || state.phase === "reveal") {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          nextRound();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.phase, handleSelect, nextRound, replay]);

  // ── Render ──

  const modeColor = PICKER_MODE_COLOR[mode];

  if (state.phase === "config") {
    if (challengeMode) return <ScaleExerciseChallengeStart onStart={startDrill} />;
    if (coached) return <ScaleExerciseCoachStart nodeId={nodeId} itemCount={coachItemCount} onStart={startDrill} />;
    return <DrillConfigScreen mode={mode} onStart={startDrill} />;
  }

  if (state.phase === "done") {
    if (coached) return <CoachSessionDoneCard />;
    if (challengeMode) {
      const times = challengeResponseTimesRef.current;
      return (
        <ChallengeScoreCard
          items={state.sessionLength}
          correct={challengeCorrectRef.current}
          averageResponseMs={times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0}
          previousBest={previousBestRef.current ?? null}
          onAgain={() => startDrill(state.config, state.sessionLength)}
          onStop={handleStop}
        />
      );
    }
    return (
      <DrillSummary
        config={state.config}
        sessionLength={state.sessionLength}
        correctCount={state.correctCount}
        sessionStats={state.sessionStats}
        onAgain={() => startDrill(state.config, state.sessionLength)}
        onStop={handleStop}
      />
    );
  }

  return (
    <DrillLoop
      state={state}
      activePip={activePip}
      donePips={donePips}
      player={audioPlayer}
      vocalModeEnabled={vocalModeEnabled}
      listenMode={listenMode}
      practiceMode={practiceMode}
      challengeMode={challengeMode}
      challengeRemainingMs={challengeRemainingMs}
      modeColor={modeColor}
      onSelect={handleSelect}
      onVocalAnswer={handleVocalAnswer}
      onNext={nextRound}
      onReplay={replay}
      onStop={handleStop}
      from={from}
    />
  );
}

// ─── Challenge mode auto-start ────────────────────────────────────────────────

function ScaleExerciseChallengeStart({
  onStart,
}: {
  onStart: (config: DrillConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart(
      { scaleType: "major", tonic: "random", fixedTonicMidi: 60, range: "full", octaves: 1, drillMode: "graded", fsrsEnabled: false },
      20,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting challenge…</p>
    </div>
  );
}

// ─── Coached listen auto-start ───────────────────────────────────────────────

function ScaleExerciseCoachStart({
  nodeId,
  itemCount,
  onStart,
}: {
  nodeId: string | undefined;
  itemCount: number;
  onStart: (config: DrillConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    const isMinor = nodeId === "diatonic-degrees-minor";
    onStart(
      {
        scaleType: isMinor ? "natural-minor" : "major",
        tonic: "random",
        fixedTonicMidi: isMinor ? 69 : 60,
        range: "full",
        octaves: 1,
        drillMode: "listening",
        fsrsEnabled: false,
      },
      itemCount,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting listen session…</p>
    </div>
  );
}

// ─── Config screen ────────────────────────────────────────────────────────────

// Preselect the scale type for the lesson's node when arriving via "Practice
// this" (`?nodeId=…`), so a minor-degrees lesson opens on the minor scale
// rather than the persisted default. Mirrors `ScaleExerciseCoachStart`.
function scaleNodeConfigPatch(nodeId: string | undefined): Partial<ScaleExerciseSavedConfig> {
  if (nodeId === "diatonic-degrees-minor") return { scaleType: "natural-minor", selectedKey: null };
  if (nodeId === "diatonic-degrees-major" || nodeId === "chromatic-degrees-major")
    return { scaleType: "major", selectedKey: null };
  return {};
}

interface DrillConfigScreenProps {
  mode: ExerciseMode;
  onStart: (config: DrillConfig, sessionLength: number) => void;
}

function DrillConfigScreen({ mode, onStart }: DrillConfigScreenProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const [initConfig] = useState(() => ({ ...loadScaleExerciseConfig(), ...scaleNodeConfigPatch(nodeId) }));

  const [sessionLength, setSessionLength] = useState(initConfig.sessionLength);
  const [showCustomLength, setShowCustomLength] = useState(
    !SESSION_LENGTH_PRESETS.includes(initConfig.sessionLength),
  );

  const modeColor = PICKER_MODE_COLOR[mode];

  const [scaleType, setScaleType] = useState<ExerciseScaleType>(initConfig.scaleType);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const combos = useMemo(() => buildComboRows(scaleType), [scaleType]);
  const [rowStats, setRowStats] = useState<Record<string, DrillStatsRecord | null>>({});

  // Restore saved key on mount; reset to null when scaleType changes.
  const savedKeyRef = useRef(initConfig.selectedKey);
  useEffect(() => {
    const restore = savedKeyRef.current;
    savedKeyRef.current = null;
    const valid = restore != null && combos.some((c) => c.statsKey === restore);
    setSelectedKey(valid ? restore : null);
    void Promise.all(
      combos.map((c) =>
        getDrillStats(c.statsKey).then((s) => [c.statsKey, s ?? null] as const),
      ),
    ).then((entries) => setRowStats(Object.fromEntries(entries)));
  }, [combos]);

  // Persist config on any change.
  useEffect(() => {
    saveScaleExerciseConfig({ sessionLength, mode, scaleType, selectedKey });
  }, [sessionLength, mode, scaleType, selectedKey]);

  const handleStart = () => {
    if (!selectedKey) return;
    const combo = combos.find((c) => c.statsKey === selectedKey);
    if (!combo) return;
    onStart(
      {
        ...combo.partialConfig,
        drillMode: mode === "listen" ? "listening" : "graded",
        fsrsEnabled: mode === "practice",
      },
      sessionLength,
    );
  };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        title="Scale Degree Recognition"
        subhead="A cadence sets the key, one note plays — name the solfège syllable."
      />

      <ModeSwitcherBar current={mode} exercisePath="/exercise/scales" />
      <ExerciseLessonLink nodeIds={lessonNodesForScaleType(scaleType)} />

      {/* Session settings */}
      <div className="space-y-6 mb-10">
        <ConfigRow label="Exercises per session">
          <div className="flex items-center gap-3 flex-wrap">
            {SESSION_LENGTH_PRESETS.map((n) => {
              const active = !showCustomLength && sessionLength === n;
              return (
                <button
                  key={n}
                  type="button"
                  aria-pressed={active}
                  onClick={() => { setSessionLength(n); setShowCustomLength(false); }}
                  className="px-4 py-2 text-sm border transition-colors"
                  style={active
                    ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor }
                    : undefined}
                >
                  {n}
                </button>
              );
            })}
            <button
              type="button"
              aria-pressed={showCustomLength}
              onClick={() => setShowCustomLength(true)}
              className="px-4 py-2 text-sm border transition-colors"
              style={showCustomLength
                ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor }
                : undefined}
            >
              Other…
            </button>
            {showCustomLength && (
              <input
                type="number"
                min={1}
                max={50}
                value={sessionLength}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 50) setSessionLength(v);
                }}
                className="w-16 px-3 py-2 text-sm border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
                aria-label="Custom number of exercises"
                autoFocus
              />
            )}
          </div>
        </ConfigRow>

      </div>

      {/* Combination picker */}
      <div>
        <div className="flex items-baseline gap-4 mb-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded shrink-0">
            Scale type
          </p>
          <div className="relative">
            <select
              value={scaleType}
              onChange={(e) => setScaleType(e.target.value as ExerciseScaleType)}
              className="pl-3 pr-7 py-1.5 bg-paper border border-ink/20 font-mono text-sm text-ink focus:outline-none focus:border-ink appearance-none cursor-pointer"
              aria-label="Select scale type"
            >
              {SCALE_TYPE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.types.map((st) => (
                    <option key={st} value={st}>
                      {SCALE_SPECS[st].displayName}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-inkFaded text-[10px]">
              ▾
            </span>
          </div>
        </div>

        <ComboList
          combos={combos}
          rowStats={rowStats}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          modeColor={modeColor}
        />
      </div>

      <button
        type="button"
        onClick={handleStart}
        disabled={!selectedKey}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: modeColor }}
      >
        Start →
      </button>
    </div>
  );
}

// ─── Drill loop ───────────────────────────────────────────────────────────────

interface DrillLoopProps {
  state: DrillState;
  activePip: number | null;
  donePips: number[];
  player: AudioPlayer | null;
  vocalModeEnabled: boolean;
  listenMode: boolean;
  practiceMode: boolean;
  challengeMode: boolean;
  challengeRemainingMs: number;
  modeColor: string;
  onSelect: (index: number) => void;
  onVocalAnswer: (optionId: string) => void;
  onNext: () => void;
  onReplay: () => void;
  onStop: () => void;
  from?: string;
}

function DrillLoop({
  state,
  activePip,
  donePips,
  player,
  vocalModeEnabled,
  listenMode,
  practiceMode,
  challengeMode,
  challengeRemainingMs,
  modeColor,
  onSelect,
  onVocalAnswer,
  onNext,
  onReplay,
  onStop,
  from,
}: DrillLoopProps): ReactNode {
  const { phase, round, roundNumber, sessionLength, config, selectedIndex, wasCorrect, wasTimeout } = state;

  const isPlaying  = phase === "playing";
  const isAwaiting = phase === "awaiting";
  const isReveal   = phase === "reveal";
  const isGraded   = phase === "graded";

  // Re-compute only when the tonic or scale type changes, not on every render.
  const scaleNotes = useMemo(
    () => buildScaleVexNotes(round?.tonicMidi ?? 60, config.scaleType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [round?.tonicMidi, config.scaleType],
  );

  const scaleLabel = SCALE_SPECS[config.scaleType].displayName;
  const rangeLabel =
    SCALE_TYPE_INFO[config.scaleType].isPentatonic ? "full" :
    config.range === "lower" ? "I–IV" :
    config.range === "upper" ? "V–VIII" : "I–VIII";

  const counterCurrent = roundNumber;
  const isLastRound = roundNumber >= sessionLength;
  const answeredCount = isGraded || isReveal ? roundNumber : roundNumber - 1;

  const buttons = round
    ? (round.syllables as string[]).map((syl, i) => {
        const isCorrect = syl === round.correctSyllable;
        const isSelected = selectedIndex === i;
        return {
          syllable: syl,
          correct:  (isGraded || isReveal) && isCorrect,
          wrong:    isGraded && isSelected && !isCorrect,
          selected: isAwaiting && isSelected,
        };
      })
    : [];

  const syntheticExercise = round
    ? drillRoundToExercise(round, config)
    : null;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8">

      {/* Back links */}
      <div className="flex items-center gap-3 mb-4">
        {from === "today" && (
          <>
            <Link
              to="/"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Today
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {from === "learn" && (
          <>
            <Link
              to="/learn"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Learn
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {!from && (
          <>
            <Link
              to="/exercise"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Ear Training
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        <button
          type="button"
          onClick={onStop}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
          aria-label="Return to drill settings"
        >
          ← Settings
        </button>
      </div>
      <ModeSwitcherBar current={challengeMode ? "challenge" : practiceMode ? "review" : listenMode ? "listen" : "practice"} />

      {/* Challenge timer */}
      {challengeMode && isAwaiting && (
        <ChallengeTimer totalMs={8000} remainingMs={challengeRemainingMs} />
      )}

      <div className="flex items-baseline justify-between mb-1">
        <h1 className="font-serif text-4xl font-medium text-ink">Scale Degree Drill</h1>
        <p className="font-mono text-xs text-inkFaded tabular-nums" aria-label={`Exercise ${counterCurrent} of ${sessionLength}`}>
          {counterCurrent} of {sessionLength}
        </p>
      </div>
      <p className="font-mono text-xs text-inkFaded mb-3">
        {scaleLabel}
        {!SCALE_TYPE_INFO[config.scaleType].isPentatonic && ` · ${rangeLabel}`}
        {config.octaves === 2 && ` · 2 oct`}
        {round ? ` · Key: ${round.keyName}` : ""}
      </p>

      {/* Scale score */}
      <div className="mb-4 overflow-x-auto">
        <Score
          notes={scaleNotes}
          width={SCALE_SPECS[config.scaleType].pattern.length <= 5 ? 380 : SCALE_SPECS[config.scaleType].pattern.length === 6 ? 440 : 500}
          height={150}
          aria-label={
            round
              ? `${round.keyName} scale, ascending`
              : `${SCALE_SPECS[config.scaleType].displayName} scale`
          }
        />
      </div>

      {/* Cadence pips */}
      <div className="h-[3px] mb-3">
        {isPlaying && <StagePips count={CADENCE_PIP_COUNT} active={activePip} done={donePips} />}
      </div>

      {/* Progress bar */}
      <div className="w-full h-0.5 bg-ink/10 mb-2" role="progressbar" aria-valuenow={counterCurrent} aria-valuemin={1} aria-valuemax={sessionLength}>
        <div
          className="h-full transition-all duration-300"
          style={{ width: `${(counterCurrent / sessionLength) * 100}%`, backgroundColor: modeColor }}
        />
      </div>

      {/* Correct / answered counter */}
      <div className="flex justify-between font-mono text-xs text-inkFaded mb-6">
        {config.drillMode !== "listening" ? (
          <span>{state.correctCount} correct of {answeredCount} answered</span>
        ) : (
          <span>{answeredCount} of {sessionLength} listened</span>
        )}
        <span>{scaleLabel}</span>
      </div>

      {/* Prompt */}
      <div className="mb-6 min-h-[3rem] flex items-center">
        {isPlaying && (
          <p className="font-serif italic text-xl text-inkSoft" aria-live="polite">
            Listen…
          </p>
        )}
        {isAwaiting && (
          <p className="font-serif italic text-xl text-ink" aria-live="polite">
            What scale degree did you hear?
          </p>
        )}
        {(isReveal || isGraded) && round && (
          <div aria-live="polite">
            {isReveal && (
              <p className="font-serif italic text-xl text-ink">
                That was{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {round.correctSyllable}
                </strong>
                .
              </p>
            )}
            {isGraded && wasCorrect && (
              <p className="font-serif italic text-xl text-ink">
                Correct —{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {round.correctSyllable}
                </strong>
                .
              </p>
            )}
            {isGraded && !wasCorrect && (
              <p className="font-serif italic text-xl text-ink">
                {wasTimeout ? "Time! " : "The answer was "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {round.correctSyllable}
                </strong>
                .
              </p>
            )}
            {isGraded && !challengeMode && (
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1" style={{ color: practiceMode ? modeColor : "#9A9390" }}>
                {practiceMode ? "Counts toward Today." : "No schedule changes."}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Solfège buttons */}
      <div className="mb-6">
        <SolfegeButtons
          buttons={buttons}
          disabled={isPlaying || isReveal || isGraded}
          onSelect={onSelect}
        />

        {/* Vocal singer — graded mode only; no guessing in listening mode */}
        {vocalModeEnabled && config.drillMode !== "listening" && syntheticExercise && (
          <VocalSinger
            exercise={syntheticExercise}
            audioPlayer={player}
            enabled={isAwaiting}
            onAnswer={onVocalAnswer}
          />
        )}

        {isAwaiting && (
          <p className="mt-2 font-mono text-[10px] text-inkFaded">
            1–7 · R to replay
          </p>
        )}
        {(isReveal || isGraded) && (
          <p className="mt-2 font-mono text-[10px] text-inkFaded">
            {isLastRound ? "Space/Enter to finish" : "Space/Enter for next"} · R
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 flex-wrap">
        {!challengeMode && (
          <button
            type="button"
            disabled={!isReveal && !isGraded}
            onClick={onNext}
            className={buttonClasses("primary")}
            style={{ backgroundColor: modeColor }}
          >
            {isLastRound ? "Finish →" : "Next →"}
          </button>
        )}
        {!challengeMode && (
          <button
            type="button"
            disabled={isPlaying}
            onClick={onReplay}
            className={buttonClasses("secondary")}
            aria-label="Replay the cadence and note"
          >
            ♩ Replay
          </button>
        )}
      </div>
      {listenMode && <ListenAutoAdvanceControl isReveal={isReveal} onAdvance={onNext} />}
    </div>
  );
}

// ─── Session summary ──────────────────────────────────────────────────────────

interface DrillSummaryProps {
  config: DrillConfig;
  sessionLength: number;
  correctCount: number;
  sessionStats: Partial<Record<number, DegreeStat>>;
  onAgain: () => void;
  onStop: () => void;
}

function DrillSummary({ config, sessionLength, correctCount, sessionStats, onAgain, onStop }: DrillSummaryProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const isGraded = config.drillMode !== "listening";
  const accuracy = isGraded ? Math.round((correctCount / sessionLength) * 100) : null;
  const degreeEntries = Object.entries(sessionStats)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([degStr, stat]) => ({ degree: Number(degStr), stat: stat! }));

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        eyebrow="scale degree"
        title="Session complete"
      />

      <div className="grid grid-cols-2 border border-ink/10 mb-8">
        <div className="p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Exercises</p>
          <p className="font-serif text-3xl font-medium text-ink">{sessionLength}</p>
        </div>
        {accuracy !== null && (
          <div className="p-6 border-l border-ink/10">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Correct</p>
            <p className="font-serif text-3xl font-medium text-ink">
              {accuracy}
              <span className="text-lg text-inkSoft font-serif italic ml-0.5">%</span>
            </p>
          </div>
        )}
      </div>

      {degreeEntries.length > 0 && (
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
            By degree
          </p>
          <div className="border border-ink/10">
            <div className="grid grid-cols-[1fr_5rem_5rem_3.5rem] px-4 py-2 border-b border-ink/10">
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Degree</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Played</span>
              {isGraded && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>}
              {isGraded && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>}
            </div>
            {degreeEntries.map(({ degree, stat }) => {
              const solfege = SCALE_SPECS[config.scaleType].solfege[degree - 1] ?? String(degree);
              const pct = isGraded && stat.played > 0
                ? Math.round((stat.correct / stat.played) * 100)
                : null;
              return (
                <div
                  key={degree}
                  className="grid grid-cols-[1fr_5rem_5rem_3.5rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0"
                >
                  <div>
                    <span className="font-serif text-sm text-ink capitalize">{solfege}</span>
                    <span className="font-mono text-xs text-inkFaded ml-2">{degree}</span>
                  </div>
                  <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.played}</span>
                  {isGraded && <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.correct}</span>}
                  {isGraded && (
                    <span className="font-mono text-xs text-inkFaded text-right tabular-nums">
                      {pct !== null ? `${pct}%` : "—"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap items-center">
        <button
          type="button"
          onClick={onAgain}
          className={buttonClasses("primary")}
        >
          Drill again →
        </button>
        <button
          type="button"
          onClick={onStop}
          className={buttonClasses("secondary")}
        >
          Change settings
        </button>
        <Link
          to="/"
          className={buttonClasses("secondary")}
        >
          Today
        </Link>
        <Link
          to="/stats"
          className={buttonClasses("secondary")}
        >
          See your stats →
        </Link>
        <ExerciseLessonLink nodeIds={lessonNodesForScaleType(config.scaleType)} />
      </div>
    </div>
  );
}

// ─── Scale score helpers ──────────────────────────────────────────────────────

// Flat-biased MIDI semitone → VexFlow letter + accidental token (lowercase).
const SEMI_TO_VEX: Record<number, { letter: string; acc: string }> = {
  0:  { letter: "c", acc: "" },
  1:  { letter: "d", acc: "b" },
  2:  { letter: "d", acc: "" },
  3:  { letter: "e", acc: "b" },
  4:  { letter: "e", acc: "" },
  5:  { letter: "f", acc: "" },
  6:  { letter: "f", acc: "#" },
  7:  { letter: "g", acc: "" },
  8:  { letter: "a", acc: "b" },
  9:  { letter: "a", acc: "" },
  10: { letter: "b", acc: "b" },
  11: { letter: "b", acc: "" },
};

function buildScaleVexNotes(tonicMidi: number, scaleType: ExerciseScaleType) {
  const { pattern } = SCALE_SPECS[scaleType];
  // Ascending scale + the octave tonic at the top.
  const offsets = [...pattern, (pattern[0] ?? 0) + 12];
  return offsets.map((offset) => {
    const midi = tonicMidi + offset;
    const semi = ((midi % 12) + 12) % 12;
    const oct = Math.floor(midi / 12) - 1;
    const { letter, acc } = SEMI_TO_VEX[semi]!;
    return { keys: [`${letter}${acc}/${oct}`], duration: "q" as const };
  });
}

// ─── Combination list ─────────────────────────────────────────────────────────

interface ComboRow {
  statsKey: string;
  keyLabel: string;
  rangeLabel: string;
  octLabel: string | null;
  partialConfig: Pick<DrillConfig, "scaleType" | "tonic" | "fixedTonicMidi" | "range" | "octaves">;
}

function buildComboRows(scaleType: ExerciseScaleType): ComboRow[] {
  const spec = SCALE_SPECS[scaleType];
  const info = SCALE_TYPE_INFO[scaleType]!;
  const rows: ComboRow[] = [];

  const tonicOptions = [
    { tonic: "fixed" as const, label: info.label },
    { tonic: "random" as const, label: "Random" },
  ];

  if (spec.isPentatonic) {
    for (const { tonic, label } of tonicOptions) {
      const partialConfig = { scaleType, tonic, fixedTonicMidi: info.midi, range: "full" as const, octaves: 1 as const };
      rows.push({ statsKey: drillStatsKey(partialConfig), keyLabel: label, rangeLabel: "–", octLabel: null, partialConfig });
    }
    return rows;
  }

  const rangeOptions = [
    { range: "lower" as const, label: "I–IV" },
    { range: "upper" as const, label: "V–VIII" },
    { range: "full"  as const, label: "I–VIII" },
  ];

  for (const { tonic, label: keyLabel } of tonicOptions) {
    for (const { range, label: rangeLabel } of rangeOptions) {
      if (range === "full") {
        for (const octaves of [1, 2] as const) {
          const partialConfig = { scaleType, tonic, fixedTonicMidi: info.midi, range, octaves };
          rows.push({ statsKey: drillStatsKey(partialConfig), keyLabel, rangeLabel, octLabel: String(octaves), partialConfig });
        }
      } else {
        const partialConfig = { scaleType, tonic, fixedTonicMidi: info.midi, range, octaves: 1 as const };
        rows.push({ statsKey: drillStatsKey(partialConfig), keyLabel, rangeLabel, octLabel: null, partialConfig });
      }
    }
  }

  return rows;
}

interface ComboListProps {
  combos: ComboRow[];
  rowStats: Record<string, DrillStatsRecord | null>;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  modeColor: string;
}

const COMBO_COLS = "grid-cols-[5rem_5rem_2rem_4rem_4rem_4rem_3rem]";

function ComboList({ combos, rowStats, selectedKey, onSelect, modeColor }: ComboListProps): ReactNode {
  return (
    <div className="border border-ink/10" role="listbox" aria-label="Choose a combination to drill">
      <div className={`grid ${COMBO_COLS} px-4 py-2 border-b border-ink/10`}>
        {(["Key", "Range", "Oct", "Listen", "Practice", "Correct", "%"] as const).map((h) => (
          <span key={h} className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">{h}</span>
        ))}
      </div>
      {combos.map((combo) => {
        const isSelected = selectedKey === combo.statsKey;
        const stats = rowStats[combo.statsKey] ?? null;
        const hasData = stats !== null && stats.played > 0;
        const listened = hasData ? Math.max(0, stats!.played - stats!.correct - stats!.errors) : null;
        const graded   = hasData ? stats!.correct + stats!.errors : null;
        const pct      = graded !== null && graded > 0
          ? Math.round((stats!.correct / graded) * 100)
          : null;
        const dim = isSelected ? "text-paper/60" : "text-inkFaded";
        return (
          <button
            key={combo.statsKey}
            type="button"
            role="option"
            aria-selected={isSelected}
            onClick={() => onSelect(combo.statsKey)}
            className={`w-full grid ${COMBO_COLS} px-4 py-3 text-left border-b border-ink/10 last:border-b-0 transition-colors ${
              isSelected ? "text-paper" : "hover:bg-ink/5 text-ink"
            }`}
            style={isSelected ? { backgroundColor: modeColor } : undefined}
          >
            <span className="font-mono text-xs">{combo.keyLabel}</span>
            <span className="font-mono text-xs">{combo.rangeLabel}</span>
            <span className={`font-mono text-xs ${dim}`}>{combo.octLabel ?? "–"}</span>
            <span className={`font-mono text-xs ${dim} tabular-nums`}>{listened ?? "–"}</span>
            <span className={`font-mono text-xs ${dim} tabular-nums`}>{graded ?? "–"}</span>
            <span className={`font-mono text-xs ${dim} tabular-nums`}>{graded !== null && graded > 0 ? stats!.correct : "–"}</span>
            <span className={`font-mono text-xs ${dim} tabular-nums`}>{pct !== null ? `${pct}%` : "–"}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function ConfigRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">
        {label}
      </p>
      {children}
    </div>
  );
}
